const MAX_STREAMS = 4;
const PARTICLES_PER_STREAM = 80;
const hash = (n) => {
  const value = Math.sin(n * 127.1 + 311.7) * 43758.5453;
  return value - Math.floor(value);
};
const finitePoint = (p) => p && ['x', 'y', 'z'].every((axis) => Number.isFinite(p[axis]));

/** One bounded translucent fluid draw follows the domain's actual swept jet
 * head, ending exactly at its measured contact. Mist is presentation only; it
 * cannot freeze a joint or extend a stream's collision reach. Caller owns GPU
 * resources through own(); repeated/paused/reduced frames have no second clock. */
export function createSkyCoolantStreams(T, scene, own, reduced = () => false) {
  const capacity = MAX_STREAMS * PARTICLES_PER_STREAM;
  const geometry = own(new T.InstancedBufferGeometry());
  geometry.setAttribute(
    'position',
    new T.Float32BufferAttribute([-0.5, -0.5, 0, 0.5, -0.5, 0, 0.5, 0.5, 0, -0.5, 0.5, 0], 3),
  );
  geometry.setAttribute('uv', new T.Float32BufferAttribute([0, 0, 1, 0, 1, 1, 0, 1], 2));
  geometry.setIndex([0, 1, 2, 0, 2, 3]);
  const offsets = new T.InstancedBufferAttribute(new Float32Array(capacity * 3), 3);
  const appearance = new T.InstancedBufferAttribute(new Float32Array(capacity * 3), 3);
  offsets.setUsage(T.DynamicDrawUsage);
  appearance.setUsage(T.DynamicDrawUsage);
  geometry.setAttribute('particleOffset', offsets);
  geometry.setAttribute('particleAppearance', appearance);
  geometry.instanceCount = 0;
  const material = own(
    new T.ShaderMaterial({
      transparent: true,
      depthWrite: false,
      depthTest: true,
      toneMapped: true,
      vertexShader: `
      attribute vec3 particleOffset;
      attribute vec3 particleAppearance;
      varying vec2 particleUv;
      varying vec2 particleStyle;
      void main() {
        particleUv = uv * 2.0 - 1.0;
        particleStyle = particleAppearance.yz;
        vec4 view = modelViewMatrix * vec4(particleOffset, 1.0);
        view.xy += position.xy * particleAppearance.x;
        gl_Position = projectionMatrix * view;
      }`,
      fragmentShader: `
      varying vec2 particleUv;
      varying vec2 particleStyle;
      void main() {
        float radius = length(particleUv);
        float edge = 1.0 - smoothstep(0.18, 1.0, radius);
        float foam = 0.85 + 0.15 * sin(particleUv.x * 9.0 + particleUv.y * 11.0);
        float alpha = edge * edge * foam * particleStyle.x;
        if (alpha < 0.005) discard;
        vec3 water = mix(vec3(0.12, 0.55, 0.67), vec3(0.69, 0.92, 0.94), particleStyle.y);
        gl_FragColor = vec4(water, alpha);
        #include <tonemapping_fragment>
        #include <colorspace_fragment>
      }`,
    }),
  );
  const mesh = new T.Mesh(geometry, material);
  mesh.name = 'Physical coolant stream spray';
  mesh.frustumCulled = false;
  mesh.visible = false;
  mesh.renderOrder = 5;
  scene.add(mesh);
  let count = 0,
    active = 0;
  function particle(x, y, z, size, alpha, foam) {
    if (count === capacity) return;
    offsets.setXYZ(count, x, y, z);
    appearance.setXYZ(count, size, alpha, foam);
    count++;
  }
  function draw(state, visible = true) {
    count = active = 0;
    const quiet = reduced();
    const streams = visible ? (state.boss?.cinderheart?.streams ?? []).slice(0, MAX_STREAMS) : [];
    for (const stream of streams) {
      if (!finitePoint(stream.from) || !finitePoint(stream.head) || !Number.isFinite(stream.age))
        continue;
      const fade = stream.done
        ? Math.max(0, 1 - (stream.age - (stream.endedAt ?? stream.age)) / 0.65)
        : 1;
      if (fade <= 0) continue;
      active++;
      const dx = stream.head.x - stream.from.x,
        dy = stream.head.y - stream.from.y,
        dz = stream.head.z - stream.from.z;
      const length = Math.hypot(dx, dy, dz);
      const droplets = quiet ? 32 : 64;
      for (let index = 0; index < droplets; index++) {
        const u = (index + 0.5) / droplets;
        const seed = (stream.id ?? 0) * 83 + index;
        const spread = Math.sin(Math.PI * u) * (0.05 + u * 0.18);
        const phase = quiet ? 0 : stream.age * 12;
        particle(
          stream.from.x + dx * u + Math.sin(seed + phase) * spread,
          stream.from.y + dy * u + Math.cos(seed * 1.7 + phase) * spread,
          stream.from.z + dz * u,
          Math.max(0.3, (length / droplets) * 1.8) * (0.85 + hash(seed) * 0.3),
          fade * (quiet ? 0.58 : 0.7),
          0.25 + hash(seed + 3) * 0.35,
        );
      }
      // The head is the exact swept contact, including an occluding armor hit.
      // A frozen contact produces pale condensation without a blinking reward.
      const mist = quiet ? 8 : 16;
      for (let index = 0; index < mist; index++) {
        const seed = (stream.id ?? 0) * 97 + index;
        const radius = (stream.done ? 0.65 : 0.18) * Math.sqrt(hash(seed));
        const angle = hash(seed + 1) * Math.PI * 2;
        particle(
          stream.head.x + Math.cos(angle) * radius,
          stream.head.y + Math.sin(angle) * radius,
          stream.head.z + (hash(seed + 2) - 0.5) * radius,
          stream.done ? 1.1 : 0.45,
          fade * 0.26,
          stream.froze ? 0.95 : 0.65,
        );
      }
    }
    geometry.instanceCount = count;
    mesh.visible = count > 0;
    offsets.needsUpdate = appearance.needsUpdate = true;
  }
  return { draw, diagnostics: () => ({ streams: active, particles: count, capacity }) };
}
